import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { confirmPasswordReset, signInWithEmailAndPassword, verifyPasswordResetCode } from "firebase/auth";
import { auth } from '../firebase';
import { publicApiClient } from '../apiClient';

const PasswordSet = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isExpiredLink, setIsExpiredLink] = useState(false);

  // Resend state
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const oobCode = new URLSearchParams(location.search).get('oobCode');

  useEffect(() => {
    if (!oobCode) {
      setError('無効なリクエストです。URLに認証コードが含まれていません。');
      setIsExpiredLink(true);
      setVerifying(false);
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then(userEmail => {
        setEmail(userEmail);
        setResendEmail(userEmail);
        setVerifying(false);
      })
      .catch(err => {
        console.error("Code verification failed:", err);
        setIsExpiredLink(true);
        setError('このパスワード設定リンクは有効期限が切れています。');
        setVerifying(false);
      });
  }, [oobCode]);

  const handlePasswordSet = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上で設定してください。');
      return;
    }
    if (!email || !oobCode) {
      setError('認証情報が無効です。ページを再読み込みするか、最初からやり直してください。');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult = await userCredential.user.getIdTokenResult();

      if (tokenResult.claims.systemadmin) {
        navigate('/admin');
      } else {
        navigate('/'); // Navigate to root, AuthGuard will handle the rest.
      }

    } catch (err) {
      console.error("Password set or sign-in failed:", err);
      let userErrorMessage = 'パスワードの設定またはログインに失敗しました。リンクの有効期限が切れている可能性があります。';
      if (err.code === 'auth/invalid-action-code') {
        setIsExpiredLink(true);
        userErrorMessage = 'このパスワード設定リンクは有効期限が切れています。';
      }
      setError(userErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      setResendError('メールアドレスを入力してください。');
      return;
    }

    setResendLoading(true);
    setResendMessage('');
    setResendError('');

    try {
      const response = await publicApiClient.post('/v1/resend-registration-email', { email: resendEmail });
      const data = await response.json();

      if (response.ok) {
        setResendMessage(data.message || 'ご指定のメールアドレスにパスワード設定メールを再送しました。');
      } else {
        setResendError(data.message || data.error || 'メールの再送信に失敗しました。');
      }
    } catch (err) {
      console.error('Resend email failed:', err);
      setResendError('ネットワークエラーが発生しました。接続を確認して再度お試しくさい。');
    } finally {
      setResendLoading(false);
    }
  };

  if (verifying) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <p>有効なリンクか確認しています...</p>
      </div>
    );
  }

  if (isExpiredLink) {
    return (
      <div style={{ width: '100%', maxWidth: '500px', margin: 'auto', padding: '20px' }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#111827',
          marginBottom: '1.5rem',
          paddingBottom: '0.5rem',
          borderBottom: '2px solid #e5e7eb'
        }}>リンク期限切れ</h1>

        <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '15px', borderRadius: '5px', marginBottom: '20px', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>このパスワード設定リンクは有効期限（1時間）が切れています。</p>
          <p>会員登録手続き（登録開始から24時間有効）はまだ有効です。以下のフォームからパスワード設定メールを再送信して、登録手続きを再開できます。</p>
        </div>

        <form onSubmit={handleResendEmail} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div>
            <label htmlFor="resendEmail" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>登録用メールアドレス</label>
            <input
              id="resendEmail"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="例: user@example.com"
              required
              style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>

          <button
            type="submit"
            disabled={resendLoading}
            style={{
              padding: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: resendLoading ? 'not-allowed' : 'pointer',
              backgroundColor: resendLoading ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '5px'
            }}
          >
            {resendLoading ? '送信中...' : 'パスワード設定メールを再送する'}
          </button>

          {resendMessage && <p style={{ color: 'green', backgroundColor: '#e6fffa', padding: '10px', borderRadius: '4px', textAlign: 'center', fontSize: '14px' }}>{resendMessage}</p>}
          {resendError && <p style={{ color: 'red', backgroundColor: '#ffe6e6', padding: '10px', borderRadius: '4px', textAlign: 'center', fontSize: '14px' }}>{resendError}</p>}
        </form>

        <div style={{ textAlign: 'center', marginTop: '25px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <p style={{ fontSize: '13px', color: '#666' }}>
            24時間以上経過している場合は、<Link to="/signup" style={{ color: '#2563eb', fontWeight: 'bold' }}>新規会員登録</Link> からおやり直しください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '500px', margin: 'auto', padding: '20px' }}>
      <h1 style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#111827',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #e5e7eb'
      }}>パスワード設定</h1>

      <form onSubmit={handlePasswordSet} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <label htmlFor="password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>新しいパスワード</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8文字以上で入力"
            required
            style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
            disabled={!email}
          />
        </div>
        <div>
          <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>パスワード（確認用）</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="もう一度入力"
            required
            style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
            disabled={!email}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email}
          style={{
            padding: '12px',
            fontSize: '18px',
            cursor: (loading || !email) ? 'not-allowed' : 'pointer',
            backgroundColor: (loading || !email) ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            marginTop: '10px'
          }}
        >
          {loading ? '設定中...' : 'パスワードを設定する'}
        </button>

        {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '1rem' }}>{error}</p>}
      </form>
    </div>
  );
};

export default PasswordSet;
